import React, { useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { Button } from "../components/common/Button";
import { Input } from "../components/common/Input";
import { Layout } from "../components/layout/Layout";
import { useSelector } from "react-redux";
import { RootState } from "../store";

const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
  padding: var(--spacing-4);
  max-width: 600px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 6rem;
  color: var(--color-primary);
  margin-bottom: var(--spacing-3);
  line-height: 1;
`;

const Subtitle = styled.h2`
  font-size: 2rem;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-4);
`;

const Message = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-6);
  line-height: 1.6;
`;

const SearchContainer = styled.div`
  width: 100%;
  max-width: 400px;
  margin-bottom: var(--spacing-6);
`;

const SuggestionsContainer = styled.div`
  margin-bottom: var(--spacing-6);
`;

const SuggestionTitle = styled.h3`
  font-size: 1.2rem;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-3);
`;

const SuggestionList = styled.div`
  display: flex;
  gap: var(--spacing-2);
  flex-wrap: wrap;
  justify-content: center;
`;

const SuggestionLink = styled.a`
  color: var(--color-primary);
  text-decoration: none;
  padding: var(--spacing-2) var(--spacing-3);
  background: rgba(226, 182, 22, 0.1);
  border-radius: 20px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(226, 182, 22, 0.2);
    transform: translateY(-1px);
  }
`;

const RedirectMessage = styled.p`
  color: var(--color-primary);
  font-size: 0.9rem;
  margin-top: var(--spacing-3);
`;

const NotFound = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [searchQuery, setSearchQuery] = React.useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate(user ? "/" : "/auth");
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate, user]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const popularPages = [
    { name: "Films Populaires", path: "/browse" },
    { name: "Séries TV", path: "/browse?type=tv" },
    { name: "Ma Liste", path: "/watchlist" },
    { name: "FAQ", path: "/faq" },
  ];

  const handleRedirect = () => {
    navigate(user ? "/" : "/auth");
  };

  return (
    <Layout>
      <NotFoundContainer>
        <Title>404</Title>
        <Subtitle>Page Non Trouvée</Subtitle>
        <Message>
          La page que vous recherchez n'existe pas ou a été déplacée. Vous
          pouvez utiliser la recherche ci-dessous ou explorer nos suggestions.
        </Message>

        <SearchContainer>
          <form onSubmit={handleSearch}>
            <Input
              icon={<Search size={20} />}
              type="text"
              placeholder="Rechercher un film ou une série..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </form>
        </SearchContainer>

        <SuggestionsContainer>
          <SuggestionTitle>Pages Populaires</SuggestionTitle>
          <SuggestionList>
            {popularPages.map((page) => (
              <SuggestionLink
                key={page.path}
                href={page.path}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(page.path);
                }}
              >
                {page.name}
              </SuggestionLink>
            ))}
          </SuggestionList>
        </SuggestionsContainer>

        <Button onClick={handleRedirect} icon={<ArrowLeft size={20} />}>
          {user ? "Retour à l'accueil" : "Aller à la connexion"}
        </Button>

        <RedirectMessage>
          Redirection automatique dans 10 secondes...
        </RedirectMessage>
      </NotFoundContainer>
    </Layout>
  );
};

export default NotFound;
